import React, { useContext, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import TextField from '@mui/material/TextField';
import DropdownSelect from './DropdownSelect';
import SupplierSelect from './SupplierSelect';
import { AppContext } from '../../App';
import { getSupplierSelectList } from '../../clients/supplier_client';
import globalSupplierSelectCache from '../../config_and_helpers/supplier_cache';
import { FilterList } from '@mui/icons-material';

/**
 * GenericSearchFilter is a reusable UI component providing search and entity filtering controls.
 *
 * Props:
 * @param {string} entityType - Type of entity to filter (e.g., 'manufacturers', 'quotes', 'suppliers').
 * @param {string|null} queryFromParam - Optional context source for filtering (e.g., 'orders', 'products').
 * @param {string} placeHolderLabel - Placeholder or label text for the dropdown selector.
 * @param {Function} onSearchInput - Callback invoked when the search input changes.
 * @param {Function} onEntityChange - Callback invoked when the entity dropdown selection changes.
 * @param {string} searchInput - Current search text value.
 * @param {string} entity - Current selected entity ID or value.
 * @param {boolean} isCompact - Whether to use a compact layout (e.g., in catalog views).
 * @param {JSX.Element|null} ModalComponent - Optional modal trigger component rendered in the left column.
 * @param {string} [activeTab] - Optional tab identifier, used for filtering contexts like quotes (e.g., 'fulfilled', 'unfulfilled').
 * @param {boolean} [showFilters] - Whether filters are currently shown.
 * @param {Function} [toggleFilters] - Function to toggle filter visibility.
 * @param {Array|null} [supplierSelectList] - Optional pre-fetched supplier select list to avoid redundant API calls.
 * @param {Array|null} [entityOptionsList] - Optional custom dropdown options (skips supplier fetch when provided).
 * @param {boolean} [hideEntityDropdown=false] - Hide the entity dropdown column.
 * @param {JSX.Element|null} [customActionButton] - Optional custom action button to render in the filter row.
 * @param {JSX.Element|null} [trailingActionButton] - Rightmost control after Filters (order-requests toolbar).
 * @returns {JSX.Element} The GenericSearchFilter component.
 */
const GenericSearchFilter = ({
  activeTab,
  entityType,
  queryFromParam = null,
  placeHolderLabel,
  onSearchInput,
  entity,
  onEntityChange,
  searchInput,
  isCompact = false,
  ModalComponent = null,
  showFilters = false,
  toggleFilters = null,
  supplierSelectList = null,
  entityOptionsList = null,
  hideEntityDropdown = false,
  customActionButton = null,
  trailingActionButton = null,
}) => {
  const { token } = useContext(AppContext);
  const [entitySelectList, setEntitySelectList] = useState([]);

  useEffect(() => {
    if (entityOptionsList != null) {
      setEntitySelectList(entityOptionsList);
      return;
    }

    if (supplierSelectList && supplierSelectList.length > 0) {
      setEntitySelectList(supplierSelectList);
      return;
    }

    if (entityType === 'quotes' && supplierSelectList !== null) {
      return;
    }

    if (entitySelectList.length) return;

    const fetchEntities = () => {
      switch (entityType) {
        case 'manufacturers':
          // Don't fetch manufacturer data automatically - only fetch when modal is opened
          setEntitySelectList([]);
          break;
        case 'quotes':
          // Don't fetch supplier data automatically - only fetch when modal is opened
          setEntitySelectList([]);
          break;
        default:
          // For suppliers, don't fetch automatically - only fetch when modal is opened
          if (entityType === 'suppliers') {
            // Don't fetch supplier data automatically
            setEntitySelectList([]);
          } else {
            // For non-supplier entity types, check if cache exists and hasn't been invalidated
            const cacheIsValid =
              globalSupplierSelectCache.data &&
              (!globalSupplierSelectCache.invalidatedAt ||
                !globalSupplierSelectCache.timestamp ||
                globalSupplierSelectCache.timestamp >
                  globalSupplierSelectCache.invalidatedAt);

            // Use global cache if available and valid
            if (cacheIsValid) {
              setEntitySelectList(globalSupplierSelectCache.data);
            } else if (
              globalSupplierSelectCache.isFetching &&
              globalSupplierSelectCache.promise
            ) {
              globalSupplierSelectCache.promise.then(() => {
                if (globalSupplierSelectCache.data) {
                  setEntitySelectList(globalSupplierSelectCache.data);
                }
              });
            } else if (
              !globalSupplierSelectCache.isFetching &&
              !globalSupplierSelectCache.data
            ) {
              globalSupplierSelectCache.isFetching = true;
              globalSupplierSelectCache.promise = getSupplierSelectList(
                token,
                data => {
                  globalSupplierSelectCache.data = data;
                  globalSupplierSelectCache.timestamp = Date.now();
                  globalSupplierSelectCache.isFetching = false;
                  setEntitySelectList(data);
                },
                queryFromParam
              );
              globalSupplierSelectCache.promise.then(() => {
                globalSupplierSelectCache.isFetching = false;
              });
            }
          }
      }
    };

    fetchEntities();
  }, [
    token,
    entityType,
    queryFromParam,
    activeTab,
    supplierSelectList,
    entityOptionsList,
    entitySelectList.length,
  ]);

  const handleSearchChange = e => {
    onSearchInput(e.target.value);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') onSearchInput(e.target.value);
  };

  const handleEntityChange = value => {
    onEntityChange(value);
  };

  const clearSupplier = () => onEntityChange('');
  return (
    <Container className="my-3">
      <Row className="align-items-center g-2">
        {ModalComponent && (
          <Col md="auto" className="m">
            {React.cloneElement(ModalComponent, {
              supplierList:
                entityType === 'suppliers' ? entitySelectList : null,
            })}
          </Col>
        )}

        <Col xs={12} md className="ms-2" style={{ minWidth: '220px' }}>
          {isCompact ? (
            <TextField
              value={searchInput}
              sx={{ width: 300 }}
              label="Search"
              onChange={handleSearchChange}
            />
          ) : (
            <InputGroup>
              <InputGroup.Text>
                <SearchIcon />
              </InputGroup.Text>
              <Form.Control
                value={searchInput}
                placeholder="Search"
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
            </InputGroup>
          )}
        </Col>

        {!hideEntityDropdown && (
          <Col xs={12} md style={{ minWidth: '220px' }}>
            {isCompact ? (
              <SupplierSelect
                supplierList={entitySelectList}
                supplier={entity}
                handleChange={e => handleEntityChange(e.target.value)}
                handleClick={clearSupplier}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  width: '100%',
                  minWidth: '220px',
                }}
              >
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <DropdownSelect
                    optionsList={entitySelectList}
                    label={placeHolderLabel || 'Supplier'}
                    selectedValue={
                      entitySelectList.find(
                        s => String(s.value) === String(entity)
                      ) || null
                    }
                    setSelectedValue={option =>
                      handleEntityChange(option?.value ?? '')
                    }
                    disabledOptions={[]}
                    clearable
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={clearSupplier}
                  style={{
                    cursor: 'pointer',
                    flexShrink: 0,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.375rem 0.75rem',
                  }}
                >
                  <RefreshIcon />
                </Button>
              </div>
            )}
          </Col>
        )}
        {customActionButton && (
          <Col xs={12} md="auto" style={{ minWidth: '200px' }}>
            {customActionButton}
          </Col>
        )}
        {toggleFilters && (
          <Col xs={12} md="auto">
            <Button
              variant={showFilters ? 'primary' : 'outline-primary'}
              onClick={toggleFilters}
              className="d-flex align-items-center"
            >
              <FilterList className="me-1" />
              Filters
            </Button>
          </Col>
        )}
        {trailingActionButton && (
          <Col xs={12} md="auto" className="ms-md-auto">
            {trailingActionButton}
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default GenericSearchFilter;
